import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CirclePlus } from 'lucide-react';

import { Form } from '@/components/ui/form';
import { formSchema } from '../_validator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import FormGeneral from './common/FormGeneral';
import Error from '@/components/common/Error';

import { usePostTax, useTaxes } from '@/data/tax';

const AddTaxDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postTax = usePostTax();
  const taxes = useTaxes();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      label: '',
      rate: ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const payload = {
        name: values.name,
        label: values.label,
        rate: Math.round(parseFloat(values.rate) * 100)
      };
      const tax = await postTax(payload);
      await taxes.mutate((currentTaxes) => [...currentTaxes, tax], false);
      setSubmitting(false);
      setIsOpen(false);
      form.reset();
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <CirclePlus className="mr-2 h-4 w-4" />
          Add Tax
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Tax</DialogTitle>
          <DialogDescription>Add a new tax rate</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormGeneral />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Add Tax</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTaxDialog;
